import { clamp01, pct } from './geometry'
import './viz.css'

export interface HeatCell {
  label: string
  value: number | null
  display: string
}

interface HeatGridProps {
  cells: HeatCell[]
  columns?: number
  maxAbs?: number
  ariaLabel: string
}

const DEFAULT_COLUMNS = 5

const hasValue = (value: number | null): value is number =>
  value !== null && Number.isFinite(value)

/**
 * Grid of single-line label/value cells shaded by sign (gain/loss) with a
 * strength proportional to |value|. A null, non-finite or zero value stays
 * untoned. The scale is maxAbs, else the largest magnitude present (at least
 * 1, so a row of sub-1 moves is not painted at full strength).
 *
 * Rows share the parent's height, up to `--heat-row-max` (default 32px, or 80px
 * in a module body in the fixed desktop tier; set it on the grid or an ancestor
 * to change it), and sit at the top: a handful of cells is one short row, a long
 * list shrinks its rows to fit (see viz.css).
 */
export function HeatGrid({ cells, columns = DEFAULT_COLUMNS, maxAbs, ariaLabel }: HeatGridProps) {
  const derived = Math.max(1, ...cells.map((c) => (hasValue(c.value) ? Math.abs(c.value) : 0)))
  const scale = maxAbs !== undefined && Number.isFinite(maxAbs) && maxAbs > 0 ? maxAbs : derived
  const cols = Number.isFinite(columns) && columns >= 1 ? Math.floor(columns) : DEFAULT_COLUMNS

  return (
    <div
      className="viz-heat"
      role="img"
      aria-label={ariaLabel}
      style={{ ['--cols' as string]: cols }}
    >
      {cells.map((cell, i) => {
        const value = hasValue(cell.value) ? cell.value : null
        const tone = value === null || value === 0 ? '' : ` viz-heat-cell--${value > 0 ? 'gain' : 'loss'}`
        const heat = value === null ? 0 : clamp01(Math.abs(value) / scale) * 100
        return (
          <div
            className={`viz-heat-cell${tone}`}
            key={`${cell.label}-${i}`}
            style={{ ['--heat' as string]: pct(heat) }}
          >
            <span className="viz-heat-label">{cell.label}</span>
            <span className="viz-heat-value">{cell.display}</span>
          </div>
        )
      })}
    </div>
  )
}
